import React, { useEffect, useState } from 'react';
import { ArrowLeftIcon, SearchIcon, CheckCircleIcon, PackageIcon, ScanLineIcon, PlayIcon, PauseIcon, XCircleIcon, ClockIcon, Leaf, DropletIcon, FlaskConicalIcon, CookingPotIcon, ThermometerIcon } from 'lucide-react';
interface CookingSystemModuleProps {
  onBackToOverview?: () => void;
}
interface Material {
  id: string;
  name: string;
  type: 'sauce' | 'spice' | 'vegetable' | 'protein';
  quantity: number;
  unit: string;
  location: string;
  scanned: boolean;
  ready: boolean; // New property to track if material is ready from pre-process
}
interface CookingOrder {
  id: string;
  name: string;
  status: 'pending' | 'in-progress' | 'completed' | 'paused';
  deadline: Date;
  startTime?: Date;
  duration?: number; // in seconds
  cookingTemp: number; // cooking temperature in °C
  cookingTime: number; // cooking time in minutes
  materials: Material[];
}
export function CookingSystemModule({
  onBackToOverview
}: CookingSystemModuleProps) {
  const [view, setView] = useState<'list' | 'detail' | 'scan' | 'production'>('list');
  const [selectedOrder, setSelectedOrder] = useState<CookingOrder | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [scannedCode, setScannedCode] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [scanSuccess, setScanSuccess] = useState<boolean | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [elapsedTime, setElapsedTime] = useState(0);
  const [timerInterval, setTimerInterval] = useState<NodeJS.Timeout | null>(null);
  const [materialToScan, setMaterialToScan] = useState<Material | null>(null);
  const [showScanPopup, setShowScanPopup] = useState(false);
  const [scanningInProgress, setScanningInProgress] = useState(false);
  const [scanPopupSuccess, setScanPopupSuccess] = useState<boolean | null>(null);
  const [scanPopupError, setScanPopupError] = useState('');
  const [scanPopupCode, setScanPopupCode] = useState('');
  // Sample orders with some materials marked as ready
  const [cookingOrders, setCookingOrders] = useState<CookingOrder[]>([{
    id: 'C1001',
    name: 'Gustoria Paella',
    status: 'pending',
    deadline: new Date(new Date().getTime() + 90 * 60000),
    cookingTemp: 95,
    cookingTime: 45,
    materials: [{
      id: 'S101',
      name: 'Paella Basissoße',
      type: 'sauce',
      quantity: 25.0,
      unit: 'l',
      location: 'Soßenlager F2',
      scanned: false,
      ready: true
    }, {
      id: 'S102',
      name: 'Paella Gewürzmischung',
      type: 'spice',
      quantity: 1.5,
      unit: 'kg',
      location: 'Gewürzlager A1',
      scanned: false,
      ready: true
    }, {
      id: 'V101',
      name: 'Paprika Mix',
      type: 'vegetable',
      quantity: 15.0,
      unit: 'kg',
      location: 'Gemüselager B2',
      scanned: false,
      ready: false
    }, {
      id: 'P101',
      name: 'Hühnerfleisch vorbereitet',
      type: 'protein',
      quantity: 18.0,
      unit: 'kg',
      location: 'Kühllager D1',
      scanned: false,
      ready: true
    }]
  }, {
    id: 'C1002',
    name: 'Gustoria Bolognese',
    status: 'in-progress',
    deadline: new Date(new Date().getTime() + 150 * 60000),
    startTime: new Date(new Date().getTime() - 30 * 60000),
    duration: 30 * 60,
    cookingTemp: 90,
    cookingTime: 60,
    materials: [{
      id: 'S201',
      name: 'Tomatensoße',
      type: 'sauce',
      quantity: 30.0,
      unit: 'l',
      location: 'Soßenlager F1',
      scanned: true,
      ready: true
    }, {
      id: 'S202',
      name: 'Italienische Kräuter',
      type: 'spice',
      quantity: 0.8,
      unit: 'kg',
      location: 'Gewürzlager A2',
      scanned: true,
      ready: true
    }, {
      id: 'V201',
      name: 'Zwiebeln gewürfelt',
      type: 'vegetable',
      quantity: 8.0,
      unit: 'kg',
      location: 'Gemüselager B1',
      scanned: true,
      ready: true
    }, {
      id: 'P201',
      name: 'Rinderhackfleisch',
      type: 'protein',
      quantity: 25.0,
      unit: 'kg',
      location: 'Kühllager D2',
      scanned: true,
      ready: true
    }]
  }, {
    id: 'C1003',
    name: 'Gustoria Curry',
    status: 'completed',
    deadline: new Date(new Date().getTime() - 60 * 60000),
    startTime: new Date(new Date().getTime() - 180 * 60000),
    duration: 120 * 60,
    cookingTemp: 85,
    cookingTime: 40,
    materials: [{
      id: 'S301',
      name: 'Kokosmilch',
      type: 'sauce',
      quantity: 20.0,
      unit: 'l',
      location: 'Soßenlager F3',
      scanned: true,
      ready: true
    }, {
      id: 'S302',
      name: 'Curry Gewürzmischung',
      type: 'spice',
      quantity: 2.0,
      unit: 'kg',
      location: 'Gewürzlager A3',
      scanned: true,
      ready: true
    }, {
      id: 'V301',
      name: 'Gemüsemischung',
      type: 'vegetable',
      quantity: 18.0,
      unit: 'kg',
      location: 'Gemüselager B3',
      scanned: true,
      ready: true
    }, {
      id: 'P301',
      name: 'Tofu gewürfelt',
      type: 'protein',
      quantity: 12.0,
      unit: 'kg',
      location: 'Kühllager D3',
      scanned: true,
      ready: true
    }]
  }]);
  // Filter orders based on search term
  const filteredOrders = cookingOrders.filter(order => order.name.toLowerCase().includes(searchTerm.toLowerCase()) || order.id.includes(searchTerm));
  // Handle order selection
  const handleOrderSelect = (order: CookingOrder) => {
    setSelectedOrder(order);
    setView('detail');
  };
  // Handle back to list
  const handleBackToList = () => {
    setView('list');
    setSelectedOrder(null);
    stopTimer();
  };
  // Handle back to detail
  const handleBackToDetail = () => {
    setView('detail');
  };
  // Handle scan mode
  const handleScanMode = () => {
    setView('scan');
    setScannedCode('');
    setScanSuccess(null);
    setErrorMessage('');
  };
  // Handle scan
  const handleScan = () => {
    if (!scannedCode || !selectedOrder) {
      setErrorMessage('Bitte geben Sie einen Barcode ein oder scannen Sie ihn');
      setScanSuccess(false);
      return;
    }
    setIsScanning(true);
    // Simulate scanning process
    setTimeout(() => {
      // Check if the scanned code matches any material
      const materialIndex = selectedOrder.materials.findIndex(material => scannedCode.includes(material.id) || scannedCode.toLowerCase().includes(material.name.toLowerCase()));
      if (materialIndex !== -1 && !selectedOrder.materials[materialIndex].scanned) {
        // Update the scanned status
        const updatedOrder = {
          ...selectedOrder
        };
        updatedOrder.materials[materialIndex].scanned = true;
        setSelectedOrder(updatedOrder);
        // Update in the orders list
        const updatedOrders = cookingOrders.map(order => order.id === updatedOrder.id ? updatedOrder : order);
        setCookingOrders(updatedOrders);
        setScanSuccess(true);
        setErrorMessage('');
      } else if (materialIndex !== -1 && selectedOrder.materials[materialIndex].scanned) {
        setScanSuccess(false);
        setErrorMessage('Dieses Material wurde bereits gescannt!');
      } else {
        setScanSuccess(false);
        setErrorMessage('Der gescannte Code gehört zu keinem benötigten Material.');
      }
      setScannedCode('');
      setIsScanning(false);
    }, 1000);
  };
  // Check if all materials are scanned
  const allMaterialsScanned = selectedOrder ? selectedOrder.materials.every(material => material.scanned) : false;
  // Start production
  const handleStartProduction = () => {
    if (!selectedOrder) return;
    const updatedOrder = {
      ...selectedOrder
    };
    updatedOrder.status = 'in-progress';
    updatedOrder.startTime = new Date();
    updatedOrder.duration = 0;
    setSelectedOrder(updatedOrder);
    // Update in the orders list
    const updatedOrders = cookingOrders.map(order => order.id === updatedOrder.id ? updatedOrder : order);
    setCookingOrders(updatedOrders);
    startTimer();
    setView('list');
  };
  // Pause production
  const handlePauseProduction = () => {
    if (!selectedOrder) return;
    const updatedOrder = {
      ...selectedOrder
    };
    if (updatedOrder.status === 'paused') {
      updatedOrder.status = 'in-progress';
      startTimer();
    } else {
      updatedOrder.status = 'paused';
      stopTimer();
    }
    setSelectedOrder(updatedOrder);
    // Update in the orders list
    const updatedOrders = cookingOrders.map(order => order.id === updatedOrder.id ? updatedOrder : order);
    setCookingOrders(updatedOrders);
  };
  // Complete production
  const handleCompleteProduction = () => {
    if (!selectedOrder) return;
    const updatedOrder = {
      ...selectedOrder
    };
    updatedOrder.status = 'completed';
    setSelectedOrder(updatedOrder);
    // Update in the orders list
    const updatedOrders = cookingOrders.map(order => order.id === updatedOrder.id ? updatedOrder : order);
    setCookingOrders(updatedOrders);
    stopTimer();
    setView('list');
  };
  // Timer functions
  const startTimer = () => {
    if (timerInterval) clearInterval(timerInterval);
    const interval = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);
    setTimerInterval(interval);
  };
  const stopTimer = () => {
    if (timerInterval) {
      clearInterval(timerInterval);
      setTimerInterval(null);
    }
  };
  // Update timer when viewing an in-progress order
  useEffect(() => {
    if (selectedOrder && selectedOrder.status === 'in-progress') {
      if (selectedOrder.duration !== undefined) {
        setElapsedTime(selectedOrder.duration);
      }
      startTimer();
    } else {
      stopTimer();
    }
    return () => {
      stopTimer();
    };
  }, [selectedOrder]);
  // Update order duration
  useEffect(() => {
    if (selectedOrder && (selectedOrder.status === 'in-progress' || selectedOrder.status === 'paused')) {
      const updatedOrder = {
        ...selectedOrder,
        duration: elapsedTime
      };
      // Update in the orders list without triggering re-renders
      setCookingOrders(prev => prev.map(order => order.id === updatedOrder.id ? updatedOrder : order));
    }
  }, [elapsedTime]);
  // Format time for display
  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor(seconds % 3600 / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  // Material type icons
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'sauce':
        return <DropletIcon size={16} className="text-amber-600" data-id="element-65" />;
      case 'spice':
        return <Leaf size={16} className="text-green-600" data-id="element-66" />;
      case 'vegetable':
        return <Leaf size={16} className="text-emerald-600" data-id="element-67" />;
      case 'protein':
        return <PackageIcon size={16} className="text-red-600" data-id="element-68" />;
      default:
        return <FlaskConicalIcon size={16} className="text-purple-600" data-id="element-69" />;
    }
  };
  // Handle opening the scan popup for a specific material
  const handleOpenScanPopup = (material: Material) => {
    setMaterialToScan(material);
    setShowScanPopup(true);
    setScanPopupCode('');
    setScanPopupSuccess(null);
    setScanPopupError('');
  };
  // Handle closing the scan popup
  const handleCloseScanPopup = () => {
    setShowScanPopup(false);
    setMaterialToScan(null);
    setScanPopupSuccess(null);
  };
  // Handle scanning in the popup
  const handleScanInPopup = () => {
    if (!scanPopupCode || !materialToScan || !selectedOrder) {
      setScanPopupError('Bitte geben Sie einen Barcode ein oder scannen Sie ihn');
      setScanPopupSuccess(false);
      return;
    }
    setScanningInProgress(true);
    // Simulate scanning process
    setTimeout(() => {
      // Check if the scanned code matches the material
      if (scanPopupCode.includes(materialToScan.id) || scanPopupCode.toLowerCase().includes(materialToScan.name.toLowerCase())) {
        // Update the scanned status
        const updatedOrder = {
          ...selectedOrder
        };
        const materialIndex = updatedOrder.materials.findIndex(m => m.id === materialToScan.id);
        if (materialIndex !== -1) {
          updatedOrder.materials[materialIndex].scanned = true;
          setSelectedOrder(updatedOrder);
          // Update in the orders list
          const updatedOrders = cookingOrders.map(order => order.id === updatedOrder.id ? updatedOrder : order);
          setCookingOrders(updatedOrders);
          setScanPopupSuccess(true);
          setScanPopupError('');
          // Close popup after success
          setTimeout(() => {
            handleCloseScanPopup();
          }, 1500);
        }
      } else {
        setScanPopupSuccess(false);
        setScanPopupError('Der gescannte Code passt nicht zu diesem Material.');
      }
      setScanningInProgress(false);
    }, 1000);
  };
  return <div className="container mx-auto" data-id="element-70">
      {/* Header */}
      <div className="mb-8" data-id="element-71">
        <h2 className="text-2xl font-bold text-gray-800 mb-2" data-id="element-72">Kochanlage</h2>
        <div className="flex items-center justify-between" data-id="element-73">
          <p className="text-gray-600" data-id="element-74">
            Steuerung und Überwachung der Kochprozesse
          </p>
          {onBackToOverview}
        </div>
      </div>
      {/* Order List View */}
      {view === 'list' && <div className="bg-white rounded-xl shadow-md p-6" data-id="element-75">
          <div className="mb-6" data-id="element-76">
            <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-77">
              Heutige Kochaufträge
            </h3>
            <p className="text-gray-600" data-id="element-78">
              Wählen Sie einen Auftrag aus, um mit dem Kochprozess zu beginnen
            </p>
          </div>
          {/* Search bar */}
          <div className="relative mb-6" data-id="element-79">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-80">
              <SearchIcon size={18} className="text-gray-400" data-id="element-81" />
            </div>
            <input type="text" className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md" placeholder="Nach Auftrag suchen..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-82" />
          </div>
          {/* Orders list */}
          <div className="space-y-4" data-id="element-83">
            {filteredOrders.length > 0 ? filteredOrders.map(order => <div key={order.id} className={`border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer 
                    ${order.status === 'completed' ? 'bg-green-50 border-green-200' : order.status === 'in-progress' ? 'bg-blue-50 border-blue-200' : order.status === 'paused' ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-200'}`} onClick={() => handleOrderSelect(order)} data-id="element-84">
                  <div className="flex justify-between items-start" data-id="element-85">
                    <div data-id="element-86">
                      <h4 className="font-medium text-gray-900" data-id="element-87">
                        {order.name}
                      </h4>
                      <p className="text-sm text-gray-500" data-id="element-88">
                        Auftrags-Nr: #{order.id}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-89">
                        Bereitstellung bis:{' '}
                        {order.deadline.toLocaleTimeString('de-DE', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}{' '}
                        Uhr
                      </p>
                      <p className="text-sm text-gray-500 flex items-center mt-1" data-id="element-90">
                        <ThermometerIcon size={14} className="mr-1" data-id="element-91" />
                        {order.cookingTemp}°C • {order.cookingTime} Min
                      </p>
                      {(order.status === 'in-progress' || order.status === 'paused') && order.duration !== undefined && <p className="text-sm text-blue-600 flex items-center mt-1" data-id="element-92">
                            <ClockIcon size={14} className="mr-1" data-id="element-93" />
                            Laufzeit: {formatTime(order.duration)}
                          </p>}
                    </div>
                    <div className="flex items-center" data-id="element-94">
                      {order.status === 'completed' ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-95">
                          <CheckCircleIcon size={12} className="mr-1" data-id="element-96" />
                          Abgeschlossen
                        </span> : order.status === 'in-progress' ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-97">
                          <CookingPotIcon size={12} className="mr-1" data-id="element-98" />
                          In Bearbeitung
                        </span> : order.status === 'paused' ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800" data-id="element-99">
                          <PauseIcon size={12} className="mr-1" data-id="element-100" />
                          Pausiert
                        </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-101">
                          <PackageIcon size={12} className="mr-1" data-id="element-102" />
                          Ausstehend
                        </span>}
                    </div>
                  </div>
                  {/* Materials progress */}
                  <div className="mt-3" data-id="element-103">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-104">
                      Materialien:{' '}
                      {order.materials.filter(m => m.scanned).length} von{' '}
                      {order.materials.length} bereitgestellt
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-105">
                      <div className="bg-[#8DC63F] h-2.5 rounded-full" style={{
                width: `${order.materials.filter(m => m.scanned).length / order.materials.length * 100}%`
              }} data-id="element-106"></div>
                    </div>
                  </div>
                </div>) : <div className="text-center py-10 bg-gray-50 rounded-lg border border-gray-200" data-id="element-107">
                <CookingPotIcon size={40} className="mx-auto text-gray-400 mb-3" data-id="element-108" />
                <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-109">
                  Keine Kochaufträge
                </h3>
                <p className="text-gray-500" data-id="element-110">
                  Für heute sind keine Kochaufträge geplant.
                </p>
              </div>}
          </div>
        </div>}
      {/* Order Detail View */}
      {view === 'detail' && selectedOrder && <div className="bg-white rounded-xl shadow-md p-6" data-id="element-111">
          <button onClick={handleBackToList} className="flex items-center gap-2 mb-6 text-gray-600 hover:text-gray-800" data-id="element-112">
            <ArrowLeftIcon size={16} data-id="element-113" />
            <span data-id="element-114">Zurück zur Übersicht</span>
          </button>
          <div className="mb-6" data-id="element-115">
            <div className="flex justify-between items-start" data-id="element-116">
              <div data-id="element-117">
                <h3 className="text-xl font-bold text-gray-800 mb-1" data-id="element-118">
                  {selectedOrder.name}
                </h3>
                <p className="text-gray-600" data-id="element-119">
                  Auftrags-Nr: #{selectedOrder.id} | Bereitstellung bis:{' '}
                  {selectedOrder.deadline.toLocaleTimeString('de-DE', {
                hour: '2-digit',
                minute: '2-digit'
              })}{' '}
                  Uhr
                </p>
                <p className="text-sm text-gray-600 flex items-center mt-1" data-id="element-120">
                  <ThermometerIcon size={14} className="mr-1" data-id="element-121" />
                  Kochtemperatur: {selectedOrder.cookingTemp}°C • Kochzeit:{' '}
                  {selectedOrder.cookingTime} Min
                </p>
                {(selectedOrder.status === 'in-progress' || selectedOrder.status === 'paused') && selectedOrder.duration !== undefined && <p className="text-sm text-blue-600 flex items-center mt-1" data-id="element-122">
                      <ClockIcon size={14} className="mr-1" data-id="element-123" />
                      Laufzeit: {formatTime(selectedOrder.duration)}
                    </p>}
              </div>
              <div data-id="element-124">
                {selectedOrder.status === 'completed' ? <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800" data-id="element-125">
                    <CheckCircleIcon size={16} className="mr-1" data-id="element-126" />
                    Abgeschlossen
                  </span> : selectedOrder.status === 'in-progress' ? <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800" data-id="element-127">
                    <CookingPotIcon size={16} className="mr-1" data-id="element-128" />
                    In Bearbeitung
                  </span> : selectedOrder.status === 'paused' ? <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-800" data-id="element-129">
                    <PauseIcon size={16} className="mr-1" data-id="element-130" />
                    Pausiert
                  </span> : <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800" data-id="element-131">
                    <PackageIcon size={16} className="mr-1" data-id="element-132" />
                    Ausstehend
                  </span>}
              </div>
            </div>
          </div>
          {/* Materials list */}
          <div className="mb-6" data-id="element-133">
            <h4 className="text-lg font-semibold text-gray-800 mb-3" data-id="element-134">
              Benötigte Materialien
            </h4>
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 mb-4" data-id="element-135">
              <div className="text-sm text-gray-600 mb-2" data-id="element-136">
                Fortschritt:{' '}
                {selectedOrder.materials.filter(m => m.scanned).length} von{' '}
                {selectedOrder.materials.length} Materialien bereitgestellt
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4" data-id="element-137">
                <div className="bg-[#8DC63F] h-2.5 rounded-full" style={{
              width: `${selectedOrder.materials.filter(m => m.scanned).length / selectedOrder.materials.length * 100}%`
            }} data-id="element-138"></div>
              </div>
              <div className="space-y-3" data-id="element-139">
                {selectedOrder.materials.map(material => <div key={material.id} className={`p-4 rounded-lg border ${material.scanned ? 'bg-green-50 border-green-200' : material.ready ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'}`} data-id="element-140">
                    <div className="flex justify-between" data-id="element-141">
                      <div data-id="element-142">
                        <div className="flex items-center" data-id="element-143">
                          {getTypeIcon(material.type)}
                          <h5 className="font-medium text-gray-900 ml-2" data-id="element-144">
                            {material.name}
                          </h5>
                          {material.ready && !material.scanned && <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full" data-id="element-145">
                              Bereit
                            </span>}
                        </div>
                        <p className="text-sm text-gray-500 mt-1" data-id="element-146">
                          {material.quantity} {material.unit} • Artikel-Nr:{' '}
                          {material.id}
                        </p>
                      </div>
                      <div className="flex flex-col items-end" data-id="element-147">
                        {material.scanned ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-148">
                            <CheckCircleIcon size={12} className="mr-1" data-id="element-149" />
                            Hinzugefügt
                          </span> : material.ready ? <button onClick={() => handleOpenScanPopup(material)} className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-xs rounded-md font-medium" data-id="element-150">
                            Rohstoff einscannen
                          </button> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-151">
                            <PackageIcon size={12} className="mr-1" data-id="element-152" />
                            Ausstehend
                          </span>}
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
          {/* Action buttons */}
          <div className="flex flex-wrap gap-3 justify-center" data-id="element-153">
            {selectedOrder.status === 'pending'}
            {selectedOrder.status === 'pending' && <button onClick={handleStartProduction} disabled={!selectedOrder.materials.some(m => m.scanned) || allMaterialsScanned} className={`bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 ${!selectedOrder.materials.some(m => m.scanned) ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-154">
                <PlayIcon size={18} data-id="element-155" />
                <span data-id="element-156">Produktion starten</span>
              </button>}
            {selectedOrder.status === 'pending' && allMaterialsScanned && <button onClick={handleStartProduction} className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2" data-id="element-157">
                <PlayIcon size={18} data-id="element-158" />
                <span data-id="element-159">Produktion starten</span>
              </button>}
            {selectedOrder.status === 'in-progress' && <>
                <button onClick={handlePauseProduction} className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2" data-id="element-160">
                  <PauseIcon size={18} data-id="element-161" />
                  <span data-id="element-162">Produktion pausieren</span>
                </button>
                <button onClick={handleCompleteProduction} className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2" data-id="element-163">
                  <CheckCircleIcon size={18} data-id="element-164" />
                  <span data-id="element-165">Produktion abschließen</span>
                </button>
              </>}
            {selectedOrder.status === 'paused' && <>
                <button onClick={handlePauseProduction} className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2" data-id="element-166">
                  <PlayIcon size={18} data-id="element-167" />
                  <span data-id="element-168">Produktion fortsetzen</span>
                </button>
                <button onClick={handleCompleteProduction} className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2" data-id="element-169">
                  <CheckCircleIcon size={18} data-id="element-170" />
                  <span data-id="element-171">Produktion abschließen</span>
                </button>
              </>}
            {selectedOrder.status === 'completed' && <div className="text-center w-full" data-id="element-172">
                <p className="text-green-600 font-medium" data-id="element-173">
                  Dieser Auftrag wurde erfolgreich abgeschlossen.
                </p>
              </div>}
          </div>
        </div>}
      {/* Scan Materials View */}
      {view === 'scan' && selectedOrder && <div className="bg-white rounded-xl shadow-md p-6" data-id="element-174">
          <button onClick={handleBackToDetail} className="flex items-center gap-2 mb-6 text-gray-600 hover:text-gray-800" data-id="element-175">
            <ArrowLeftIcon size={16} data-id="element-176" />
            <span data-id="element-177">Zurück zum Auftrag</span>
          </button>
          <div className="mb-6" data-id="element-178">
            <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-179">
              Materialien scannen für {selectedOrder.name}
            </h3>
            <p className="text-gray-600" data-id="element-180">
              Bitte scannen Sie die Materialien, die Sie zur Kochanlage gebracht
              haben.
            </p>
          </div>
          {/* Scan area */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6" data-id="element-181">
            <h4 className="font-medium text-gray-800 mb-4 flex items-center" data-id="element-182">
              <ScanLineIcon size={20} className="mr-2" data-id="element-183" />
              Materialien scannen
            </h4>
            <div className="flex flex-col md:flex-row gap-3 mb-4" data-id="element-184">
              <div className="relative flex-grow" data-id="element-185">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-186">
                  <ScanLineIcon size={18} className="text-gray-400" data-id="element-187" />
                </div>
                <input type="text" className={`pl-10 pr-4 py-2 w-full border ${scanSuccess === true ? 'border-green-500 bg-green-50' : scanSuccess === false ? 'border-red-500 bg-red-50' : 'border-gray-300'} rounded-md`} placeholder="Barcode scannen..." value={scannedCode} onChange={e => setScannedCode(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleScan()} data-id="element-188" />
              </div>
              <button onClick={handleScan} disabled={isScanning} className={`py-2 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-md font-medium ${isScanning ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-189">
                {isScanning ? 'Scanne...' : 'Scannen'}
              </button>
            </div>
            {scanSuccess === true && <div className="mb-4 flex items-center text-green-600 text-sm" data-id="element-190">
                <CheckCircleIcon size={16} className="mr-1" data-id="element-191" />
                <span data-id="element-192">Material erfolgreich gescannt!</span>
              </div>}
            {scanSuccess === false && <div className="mb-4 flex items-center text-red-600 text-sm" data-id="element-193">
                <XCircleIcon size={16} className="mr-1" data-id="element-194" />
                <span data-id="element-195">{errorMessage}</span>
              </div>}
            {/* Materials checklist */}
            <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-196">
              <h5 className="font-medium text-gray-800 mb-3" data-id="element-197">
                Material-Checkliste:
              </h5>
              <div className="space-y-2" data-id="element-198">
                {selectedOrder.materials.map(material => <div key={material.id} className={`flex items-center p-3 rounded-lg border ${material.scanned ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`} data-id="element-199">
                    <div className={`w-5 h-5 rounded-full mr-3 flex items-center justify-center ${material.scanned ? 'bg-green-500 text-white' : 'border border-gray-400'}`} data-id="element-200">
                      {material.scanned && <CheckCircleIcon size={14} data-id="element-201" />}
                    </div>
                    <div className="flex-1" data-id="element-202">
                      <div className="flex items-center" data-id="element-203">
                        {getTypeIcon(material.type)}
                        <div className="font-medium ml-2" data-id="element-204">{material.name}</div>
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-205">
                        {material.quantity} {material.unit} • Artikel-Nr:{' '}
                        {material.id}
                      </div>
                    </div>
                    <div data-id="element-206">
                      {material.scanned ? <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full" data-id="element-207">
                          Bereitgestellt
                        </span> : <span className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded-full" data-id="element-208">
                          Ausstehend
                        </span>}
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
          {/* Action buttons */}
          <div className="flex justify-center" data-id="element-209">
            {allMaterialsScanned ? <button onClick={handleStartProduction} className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-medium flex items-center gap-2" data-id="element-210">
                <PlayIcon size={18} data-id="element-211" />
                <span data-id="element-212">Produktion starten</span>
              </button> : <button onClick={handleBackToDetail} className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-medium" data-id="element-213">
                Zurück zum Auftrag
              </button>}
          </div>
        </div>}
      {/* Scan Material Popup */}
      {showScanPopup && materialToScan && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-214">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4" data-id="element-215">
            <div className="flex justify-between items-center mb-4" data-id="element-216">
              <h3 className="text-lg font-bold text-gray-800" data-id="element-217">
                Material einscannen
              </h3>
              <button onClick={handleCloseScanPopup} className="text-gray-500 hover:text-gray-700" data-id="element-218">
                <XCircleIcon size={20} data-id="element-219" />
              </button>
            </div>
            <div className="mb-4" data-id="element-220">
              <div className="flex items-center mb-2" data-id="element-221">
                {getTypeIcon(materialToScan.type)}
                <p className="font-medium text-gray-900 ml-2" data-id="element-222">
                  {materialToScan.name}
                </p>
              </div>
              <p className="text-sm text-gray-600" data-id="element-223">
                Bitte scannen Sie den Barcode für {materialToScan.name}{' '}
                (Artikel-Nr: {materialToScan.id})
              </p>
            </div>
            <div className="mb-4" data-id="element-224">
              <div className="flex gap-2" data-id="element-225">
                <input type="text" className={`flex-1 px-3 py-2 border rounded-md ${scanPopupSuccess === true ? 'border-green-500 bg-green-50' : scanPopupSuccess === false ? 'border-red-500 bg-red-50' : 'border-gray-300'}`} placeholder="Barcode scannen..." value={scanPopupCode} onChange={e => setScanPopupCode(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleScanInPopup()} data-id="element-226" />
                <button onClick={handleScanInPopup} disabled={scanningInProgress} className={`px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md ${scanningInProgress ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-227">
                  {scanningInProgress ? 'Scanne...' : 'Scannen'}
                </button>
              </div>
              {scanPopupSuccess === true && <div className="mt-2 text-green-600 flex items-center" data-id="element-228">
                  <CheckCircleIcon size={16} className="mr-1" data-id="element-229" />
                  <span data-id="element-230">Material erfolgreich gescannt!</span>
                </div>}
              {scanPopupSuccess === false && <div className="mt-2 text-red-600 flex items-center" data-id="element-231">
                  <XCircleIcon size={16} className="mr-1" data-id="element-232" />
                  <span data-id="element-233">{scanPopupError}</span>
                </div>}
            </div>
          </div>
        </div>}
    </div>;
}